import { type FC, useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { nanoid } from 'nanoid'
import { AudioWave } from 'shared/ui/AudioPlayer/ui/AudioWave'
import { IconButton, type IIconButtonSize, showToast, SpeedMediaBtn, Typography } from 'shared/ui'
import { getAriaLabel } from 'shared/lib'
import { AudioPlayerActions } from 'shared/ui/AudioPlayer/ui/AudioPlayerActions'
import {
  type IPlayerColorType,
  type IPlayerSizeType,
  type IResponseRecordTranscriptStatus,
} from 'shared/ui/AudioPlayer/ui/types'
import { SectionBottom } from 'shared/ui/AudioPlayer/ui/SectionBottom/SectionBottom'
import { AudioProcessing } from 'shared/ui/AudioPlayer/ui/AudioProcessing.tsx'
import { uiStore } from 'shared/store/uiStore.ts'
import { useDuration } from 'shared/ui/AudioPlayer/hooks/useDuration.ts'
import { type IUseWaveformApiRef } from 'shared/ui/AudioPlayer/ui/AudioWave/hooks/useWaveformApiRef.ts'
import { AudioPlayerError } from 'shared/ui/AudioPlayer/ui/AudioPlayerError/AudioPlayerError.tsx'
import styles from './styles.module.scss'
import { PlayAttachmentButton, PlayDefaultButton } from './AudioPlayButton'

export type IAudioPlayerTranscriptItem = {
  id: number
  speaker_label: string
  speaker_number: number
  text: string
}

export type IAudioPlayerSummaryItem = {
  id: number
  title: string
  items: {
    id: number
    text: string
  }[]
}

type IAudioPlayerRecord = {
  id: number
  record_id: string
  sid: string
}

export type IHandleTrackOrganizationActivity = {
  event: string
  data?: {
    [key: string]: string | number | number[]
  }
}

export type AudioPlayerProps = {
  url?: string
  fileName?: string
  summary?: IAudioPlayerSummaryItem[]
  transcriptions?: IAudioPlayerTranscriptItem[]
  transcriptStatus?: IResponseRecordTranscriptStatus
  is_auto_transcribe?: boolean
  fromAttachMedia?: boolean
  widthWave?: number
  heightWave?: number
  isClosed?: boolean
  handlePlaying?: (value: boolean) => void
  colorType?: IPlayerColorType
  size?: IPlayerSizeType
  fullWidth?: boolean
  noRightActions?: boolean
  autoplay?: boolean
  togglePlaying?: (value: boolean) => void
  setScrollBottomTrigger?: () => void
  retryTranscript?: () => void
  setIsError?: (value: boolean) => void
  className?: string
  canTranscript?: boolean
  disableTranscript?: boolean
  hideTranscriptionButton?: boolean
  fromCallsHistory?: boolean
  isCallAudio?: boolean
  record?: IAudioPlayerRecord
  contactId?: number
  handleTrackOrganizationActivity?: (data: IHandleTrackOrganizationActivity) => void
  isHipaaCompliant?: boolean
  hidePlayerActions?: boolean
  source?: string
  duration?: number
  onError?: (e: unknown) => void
}

export const AudioPlayer: FC<AudioPlayerProps> = observer((props) => {
  const {
    url,
    summary = [],
    transcriptions = [],
    transcriptStatus = null,
    fromAttachMedia,
    widthWave,
    heightWave = 24,
    isClosed = false,
    handlePlaying,
    colorType = 'white',
    size = 'middle',
    fullWidth,
    noRightActions,
    autoplay,
    togglePlaying,
    className,
    hideTranscriptionButton = false,
    fromCallsHistory,
    isCallAudio = false,
    record,
    contactId,
    handleTrackOrganizationActivity,
    isHipaaCompliant,
    hidePlayerActions = false,
    duration,
    setIsError,
    onError,
  } = props
  const [id] = useState(() => nanoid(12))
  const [loading, setLoading] = useState(!url)
  const [loadingMD, setLoadingMD] = useState(false)
  const [showTranscription, setShowTranscription] = useState(false)
  const [speed, setSpeed] = useState(1)
  const [playing, setPlaying] = useState(!!autoplay)
  const [error, setError] = useState(false)
  const showShortView = isClosed && !playing
  const loadingAll = loading || loadingMD
  const disabled = !url || loadingAll
  const waveformRef = useRef<IUseWaveformApiRef | null>(null)
  const { time, setTime } = useDuration({
    url: url,
    duration: duration,
    setLoadingMD: setLoadingMD,
  })

  useEffect(() => {
    setLoading(!url)
  }, [url])

  const handleError = useCallback((value: boolean) => {
    if (value) {
      showToast({
        type: 'error',
        title: 'Something went wrong. Please try again',
      })
      setPlaying(false)
      setLoading(false)
    }

    setError(value)
    setIsError?.(value)
  }, [])

  const onPlay = async () => {
    if (disabled) return

    uiStore.setPlayAudioId(id)

    handleError(false)

    waveformRef.current?.onWaveformPlay({
      playing: playing,
      onStart: () => {
        setLoading(true)
      },
      onFinish: () => {
        setPlaying(!playing)
        handlePlaying?.(!playing)
        setLoading(false)
      },
      onError: (error) => {
        handleError(true)
        onError?.(error)
      },
    })
  }

  const onRetry = () => {
    handleError(false)
    onPlay()
  }

  const onSpeed = (speed: number) => {
    setSpeed(speed)
    waveformRef.current?.waveform?.setPlaybackRate(speed)
  }

  const PlayButton = fromAttachMedia ? PlayAttachmentButton : PlayDefaultButton

  const iconButtonSize = (): IIconButtonSize => {
    if (size === 'small') return 'small'

    return 'medium'
  }

  useEffect(() => {
    togglePlaying?.(playing)
    if (isCallAudio && isHipaaCompliant && playing && record?.record_id) {
      handleTrackOrganizationActivity?.({
        event: 'phi.call.recording.accessed',
        data: {
          id: record.id,
          recording_id: record.record_id,
        },
      })
    }
  }, [playing, isCallAudio])

  useEffect(() => {
    if (isCallAudio && isHipaaCompliant && showTranscription && record?.sid && contactId) {
      handleTrackOrganizationActivity?.({
        event: 'phi.call.transcript.viewed',
        data: {
          call_id: record?.sid,
          contact_id: contactId,
        },
      })
    }
  }, [showTranscription, isCallAudio])

  return (
    <>
      <div
        className={classNames(
          styles.wrapAudio,
          styles[`size_${size}`],
          styles[`color_${colorType}`],
          {
            [styles.fromAttachMedia]: fromAttachMedia,
            [styles.fullWidth]: fullWidth,
            [styles.noRightActions]: noRightActions,
            [styles.fromCallsHistory]: fromCallsHistory,
            [styles.error]: isClosed && error,
          },
          className
        )}
        aria-label={getAriaLabel('AudioPlayer')}
      >
        <PlayButton
          isPlaying={playing}
          onClick={onPlay}
          size={iconButtonSize()}
          disabled={disabled}
        />

        <AudioProcessing
          hideProgressLabel={isClosed}
          loading={loadingAll}
          duration={time}
          setDuration={setTime}
        />

        {isClosed && error && (
          <IconButton
            className={styles.errorClose}
            ariaLabel={'error'}
            size={'mini'}
            variant={'icon'}
            icon={'close'}
            transparentBackground
            iconProps={{
              fontSize: 16,
              className: styles.errorClose,
            }}
            tooltipProps={{
              label: (
                <>
                  Failed to load <br />
                  <Typography
                    tag={'a'}
                    variant={'body-md-medium'}
                    className={styles.errorClose__link}
                    onClick={onRetry}
                    ariaLabel={'link'}
                  >
                    Retry
                  </Typography>
                </>
              ),
            }}
          />
        )}

        <div
          className={classNames(styles.wave, {
            [styles['wave__loading']]: loadingAll,
          })}
        >
          {url && (
            <AudioWave
              ref={waveformRef}
              url={url}
              id={id}
              autoplay={autoplay}
              height={heightWave}
              playing={playing}
              setPlaying={setPlaying}
              setDuration={setTime}
              width={widthWave}
              hide={showShortView}
            />
          )}
        </div>

        <div className={styles.actions}>
          {!fromAttachMedia && !showShortView && !noRightActions && (
            <>
              <div className={styles.wrapSpeed}>
                <SpeedMediaBtn fromAudioPlayer speed={speed} setSpeed={onSpeed} />
              </div>
              {!hidePlayerActions && (
                <AudioPlayerActions
                  {...props}
                  showTranscription={showTranscription}
                  setShowTranscription={setShowTranscription}
                  hideTranscriptionButton={hideTranscriptionButton}
                />
              )}
            </>
          )}
        </div>
      </div>

      {!isClosed && error && <AudioPlayerError onRetry={onRetry} />}

      {showTranscription && (
        <SectionBottom
          transcriptStatus={transcriptStatus}
          transcriptions={transcriptions}
          summary={summary}
        />
      )}
    </>
  )
})
